import { computed, type Ref } from 'vue';
import type { ApplicantEntry } from '@/types/Applicant';

export const NEW_APPLICANT_ID = '__new_applicant__';

const formatDisplayName = (a: ApplicantEntry) => `${a.firstName} ${a.lastName}`;

/**
 * Composable for building the "Select Existing Applicant" dropdown options.
 * Returns a Record<string, string> for BxSelect compatibility.
 *
 * @param allIndividuals - All individuals in the application
 * @param excludedIds - IDs to exclude (e.g., already used in this entity)
 * @param selectedId - Currently selected ID (always included so BxSelect can display label)
 * @param isSavedRecord - Whether the current record is already saved (hides "+ Add new applicant")
 */
export const useExistingApplicantSelector = (
  allIndividuals: Ref<ApplicantEntry[]>,
  excludedIds: Ref<string[]>,
  selectedId: Ref<string | undefined>,
  isSavedRecord: Ref<boolean> = { value: false } as Ref<boolean>,
) => {
  const dropdownOptions = computed(() => {
    const options: Record<string, string> = {};

    // Add existing applicants (exclude already used, but always include currently selected)
    allIndividuals.value
      .filter((a) => a.firstName && a.lastName)
      .filter((a) => a.id === selectedId.value || !excludedIds.value.includes(a.id))
      .forEach((a) => {
        options[a.id] = formatDisplayName(a);
      });

    // Only show "+ Add new applicant" for new records (not yet saved)
    if (!isSavedRecord.value) {
      options[NEW_APPLICANT_ID] = '+ Add new applicant';
    }

    return options;
  });

  const findApplicantById = (id: string): ApplicantEntry | undefined => {
    if (!id || id === NEW_APPLICANT_ID) return undefined;
    return allIndividuals.value.find((a) => a.id === id);
  };

  const isNewApplicant = (selectedId: string): boolean => {
    return selectedId === NEW_APPLICANT_ID;
  };

  return {
    dropdownOptions,
    findApplicantById,
    isNewApplicant,
  };
};
